import { ActionTree }                                                   from 'vuex';
import { cloneDeep }                                                    from 'lodash-es';
import { useGlobals }                                                   from '@/hooks/use-globals';
import { RootState }                                                    from '@/store/store.types';
import i18n                                                             from '@/i18n';
import { ObjectWithStringKeys }                                         from '@/types';
import { Notification }                                                 from '@/components/notification/notification';
import { handleClientBasicError }                                       from '@/helpers/helpers';
import { XAxisReportPanelGroups }                                       from '@/views/x-axis-report/report-edit/helpers/x-axis-report.variables';
import { getXAxisViewMode }                                             from '@/views/x-axis-report/report-edit/helpers/x-axis.report-general.helpers';
import { BuilderConfiguration, XAxisReportLayout, XAxisSeriesResponse } from '@/views/x-axis-report/report-edit/types/types';
import { getSortedConfiguration,
  isConfigurationValid }                                                                       from '@/views/x-axis-report/report-edit/helpers/side-panel/x-axis-report-side-panel.helpers';
import { calculateReport, postXAxisReport, putXAxisReport }             from '@/views/x-axis-report/report-edit/service/x-axis-report-edit.service';
import { FilterOption }                                                 from '@/components/filter-panel/filter-panel.types';
import { getConfigurationRequestBody }                                  from '@/views/x-axis-report/report-edit/helpers/api-configuration/configuration-request-body';
import { getCalculateConfiguration }                                    from '@/views/x-axis-report/report-edit/helpers/api-configuration/calculate-configuration';

const actions: ActionTree<XAxisReportLayout, RootState> = {
  async clearElements({ commit, dispatch }): Promise<void> {
    try {
      commit('clearElements');
      commit('clearSelectedFilters');

      await dispatch('calculateReport');
    } catch (e) {
      throw Error(e);
    }
  },
  async closeFilterOption({ dispatch }, filterOption: FilterOption): Promise<void> {
    try {
      dispatch('manageDisabledFilter', { filterOption, removeFilter: true });
    } catch (e) {
      throw Error(e);
    }
  },
  async clearSelectedFilters({ dispatch, commit }): Promise<void> {
    try {
      commit('setActiveGroupName', XAxisReportPanelGroups.FILTERS);

      dispatch('clearElements');
    } catch (e) {
      throw Error(e);
    }
  },
  async manageDisabledFilter({ commit, state, dispatch }, payload: any): Promise<void> {
    const { filterOption, removeFilter } = payload;

    commit('setActiveGroupName', XAxisReportPanelGroups.FILTERS);
    try {
      const isFilterOptionDisabled: boolean = state.disabledFilters.some((disabledFilter: FilterOption) => (
        disabledFilter.uuid === filterOption.uuid && disabledFilter.valueName === filterOption.valueName
      ));

      if (isFilterOptionDisabled) {
        if (removeFilter) {
          commit('setDisabledFilter', filterOption);
        } else {
          commit('addSingleOptionToSelectedFilter', filterOption);
          commit('setDisabledFilter', filterOption);
        }
      } else if (removeFilter) {
        commit('removeSingleOptionFromSelectedFilter', filterOption);
      } else {
        commit('setDisabledFilter', filterOption);
        commit('removeSingleOptionFromSelectedFilter', filterOption);
      }

      commit('updateFilterValues', filterOption);
      await dispatch('calculateReport');
    } catch (e) {
      throw Error(e);
    }
  },
  async saveXAxisReport({ commit, rootState, state, getters }, payload: { isEditMode: boolean; uuid: string | undefined; isStyleOverrideMode: boolean; name?: string }): Promise<void> {
    const { router } = useGlobals();

    try {
      commit('setSaveReportButtonLoading', true);
      const configuration: ObjectWithStringKeys<BuilderConfiguration[]> = payload.isStyleOverrideMode ? state.panelDefaultConfiguration : state.sidePanelCalculateConfiguration;
      const requestBody: ObjectWithStringKeys<BuilderConfiguration[]> = getSortedConfiguration(getConfigurationRequestBody(configuration, state.groups));

      if (!payload.isEditMode && !payload.isStyleOverrideMode) {
        const { data }: { data: { uuid: string } } = await postXAxisReport({
          ...requestBody,
          title: getters.getReportName,
        });

        await router.push({
          name: payload.name,
          params: {
            xAxisReportUuid: data.uuid,
          },
        });
      } else {
        await putXAxisReport({
          ...requestBody,
          title: getters.getReportName ?? rootState.views.vXAxisReportPreview.reportName,
        }, payload.uuid as string);

        commit('setSaveReportButtonLoading', false);
      }

      Notification.success(
        i18n.t(`${rootState.i18n.GLOBAL_PATH}.success.success`),
        i18n.t(`${getXAxisViewMode()}.success.${payload.isEditMode ? 'updated' : 'created'}`),
      );
    } catch (e) {
      handleClientBasicError(e);
    }
  },
  async calculateReport({ commit, state }): Promise<void> {
    try {
      commit('setFiltersChanged', true);
      commit('setChartLoading', true);
      commit('setPanelButtonLoading', true);
      const configuration: ObjectWithStringKeys<BuilderConfiguration[]> = getSortedConfiguration(getCalculateConfiguration(state.groups));
      commit('setConfiguration', cloneDeep(configuration));

      if (isConfigurationValid(state.sidePanelCalculateConfiguration)) {
        const { data }: { data: { xaxisSeries: XAxisSeriesResponse[] } } = await calculateReport(configuration);
        commit('setChartConfiguration', data.xaxisSeries);
      }
    } catch (e) {
      throw Error(e);
    } finally {
      commit('setFiltersChanged', false);
      commit('setChartLoading', false);
      commit('setPanelButtonLoading', false);
      commit('setIsChartSaved', true);
    }
  },
};

export default actions;
